import styled from "styled-components";
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Loader } from "semantic-ui-react";
import { Link } from "react-router-dom";
import { fetchEstates } from "../../redux/modules/estates";
import { reverse, routes } from "../../routes";
import { Currency } from "../../containers/common/currency";
import { PayByCardAction } from "./pay-by-card";
import { device } from "../../config";
import { t } from "../../constant/translations";

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a8a29e;
  margin: 0 0 8px 0;
`;

const Card = styled.div`
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const EstateRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 14px;
  gap: 16px;

  & + & {
    border-top: 1px solid #f4f4f5;
  }

  @media ${device.handheld} {
    padding: 16px 20px;
    gap: 32px;
  }
`;

const EstateInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const EstateName = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: #18181b;

  @media ${device.handheld} {
    font-size: 14px;
  }
`;

const EstateId = styled.div`
  font-size: 12px;
  color: #52525b;
  margin-top: 2px;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const StatLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: #a1a1aa;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;

  @media ${device.handheld} {
    font-size: 11px;
  }
`;

const StatValue = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #18181b;
  white-space: nowrap;
  letter-spacing: -0.01em;

  @media ${device.handheld} {
    font-size: 15px;
  }
`;

const EstateOwnerSummary = ({ block }) => {
  const estates = useSelector((state) => state.estates.entities);
  const loading = useSelector((state) => state.estates.pending);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchEstates({ block: block.id, owned: true }));
  }, [block.id, dispatch]);

  if (loading) {
    return <Loader active inline="centered" />;
  }

  if (!estates || estates.length === 0) {
    return null;
  }

  const cardEnabled = block.config.billing.card_settlement_enabled;

  return (
    <div>
      <SectionLabel>{t("estate.liab_own_estate")}</SectionLabel>
      <Card>
        {estates.map((estate) => (
          <EstateRow key={estate.id}>
            <EstateInfo>
              <EstateName>
                <Link
                  to={reverse(routes.estateDetails, {
                    blockId: block.id,
                    estateId: estate.id,
                  })}
                >
                  {estate.name}
                </Link>
              </EstateName>
              <EstateId>
                {t("estate.branch_numb")}: {estate.vendor_id}
              </EstateId>
            </EstateInfo>
            <Stat>
              <StatLabel>{t("billing.obligations")}</StatLabel>
              <StatValue>
                <Currency amount={estate.debt} />
              </StatValue>
            </Stat>
            <Stat>
              <StatLabel>{t("charge.balance")}</StatLabel>
              <StatValue>
                <Currency amount={estate.balance} />
              </StatValue>
            </Stat>
            {cardEnabled && (
              <PayByCardAction
                estate={estate}
                prepayment={parseFloat(estate.debt) === 0}
              />
            )}
          </EstateRow>
        ))}
      </Card>
    </div>
  );
};

export default EstateOwnerSummary;
